import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { confirmAccount } from "../_redux/authCrud";
import { actions as authActions } from "../_redux/authRedux";

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 32;

export const getConfirmationToken = search => {
  const token = new URLSearchParams(search || "").get("token");
  return token && token.trim() ? token.trim() : null;
};

export const getConfirmationErrorCode = error => {
  const data = error && error.response && error.response.data;
  return data && (data.code || data.error_code);
};

export function ConfirmRegistration() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const token = useMemo(
    () => getConfirmationToken(location.search),
    [location.search]
  );
  const [step, setStep] = useState(token ? "validating" : "invalid");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const finishConfirmation = useCallback(() => {
    // The invitation may be opened while another backoffice account is
    // authenticated in this browser. Clear that session before navigating so
    // `/auth/login` cannot be intercepted by the authenticated-route redirect.
    Cookies.remove("session_token", { path: "/" });
    dispatch(authActions.logout());
    history.replace("/auth/login");
  }, [dispatch, history]);

  useEffect(() => {
    let mounted = true;

    if (!token) {
      setStep("invalid");
      return () => {
        mounted = false;
      };
    }

    confirmAccount(token)
      .then(() => {
        if (mounted) {
          finishConfirmation();
        }
      })
      .catch(error => {
        if (!mounted) {
          return;
        }
        if (getConfirmationErrorCode(error) === "need_password") {
          setStep("password");
          return;
        }
        setStep("invalid");
      });

    return () => {
      mounted = false;
    };
  }, [finishConfirmation, token]);

  const submitPassword = async event => {
    event.preventDefault();
    setFormError("");

    if (password.length < MIN_PASSWORD_LENGTH || password.length > MAX_PASSWORD_LENGTH) {
      setFormError("La contraseña debe tener entre 8 y 32 caracteres.");
      return;
    }
    if (password !== repeatPassword) {
      setFormError("Las contraseñas no coinciden.");
      return;
    }

    setSubmitting(true);
    try {
      await confirmAccount(token, password);
      finishConfirmation();
    } catch (_error) {
      setFormError(
        "No pudimos completar la activación. El enlace puede haber vencido o ya haber sido utilizado."
      );
      setSubmitting(false);
    }
  };

  if (step === "validating") {
    return (
      <div className="login-form login-signin text-center" role="status">
        <span className="spinner spinner-primary mr-4" aria-hidden="true" />
        Validando invitación…
      </div>
    );
  }

  if (step === "invalid") {
    return (
      <div className="login-form login-signin text-center">
        <h3 className="font-size-h3 mb-5">No pudimos validar la invitación</h3>
        <p className="text-muted mb-8">
          El enlace es inválido, venció o ya fue utilizado.
        </p>
        <Link className="btn btn-primary font-weight-bold px-9 py-4" to="/auth/login">
          Ir al inicio de sesión
        </Link>
      </div>
    );
  }

  return (
    <div className="login-form login-signin">
      <div className="text-center mb-10">
        <h3 className="font-size-h3">Activar cuenta</h3>
        <p className="text-muted mt-3 mb-0">
          Creá una contraseña para completar tu acceso al panel.
        </p>
      </div>

      <form
        onSubmit={submitPassword}
        className="form fv-plugins-bootstrap fv-plugins-framework"
      >
        {formError ? (
          <div className="mb-7 alert alert-custom alert-light-danger" role="alert">
            <div className="alert-text font-weight-bold">{formError}</div>
          </div>
        ) : null}

        <div className="form-group fv-plugins-icon-container">
          <label htmlFor="confirmation-password">Contraseña</label>
          <input
            id="confirmation-password"
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            required
            className="form-control form-control-solid h-auto py-5 px-6"
            value={password}
            onChange={event => setPassword(event.target.value)}
          />
        </div>

        <div className="form-group fv-plugins-icon-container">
          <label htmlFor="confirmation-repeat-password">Repetir contraseña</label>
          <input
            id="confirmation-repeat-password"
            name="repeatPassword"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            required
            className="form-control form-control-solid h-auto py-5 px-6"
            value={repeatPassword}
            onChange={event => setRepeatPassword(event.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary font-weight-bold px-9 py-4 my-3 w-100"
        >
          Completar activación
          {submitting ? <span className="ml-3 spinner spinner-white" /> : null}
        </button>
      </form>
    </div>
  );
}

export default ConfirmRegistration;
